import { ResourceItem } from "./ResourceItem";
import { User } from "./iam/User";
import {
  apiField,
  itemApiField,
  dateApiField,
  itemArrayApiField,
  constructFromApiData,
  getApiPostData,
} from "./ResourceItemMetadata";
import * as apps from "./apps";
import { RevisionActions } from "../revision";
import { Resource } from "../resource";
import MegaraxClient from "../megaraxClient";
import {
  createDeleteResource,
  createListResource,
  createDetailResource,
} from "../actions";

export class DecisionVote extends ResourceItem {
  user?: User;
  status?: boolean;
  decisionId?: number;

  constructor(data?: any) {
    super(data);

    if (data) {
      this.user = new User(data.user);
      this.status = data.status;
    }
  }

  getPostData() {
    return {
      status: this.status,
      decision: this.decisionId,
    };
  }

  toString() {
    return this.user!.toString() + " vote";
  }
}

export class DecisionComment extends ResourceItem {
  user!: User;
  text!: string;
  createdAt!: Date;

  constructor(data?: any) {
    super(data);

    if (data) {
      this.user = new User(data.user);
      this.text = data.text;
      this.createdAt = new Date(data.createdAt);
    }
  }

  getPostData() {
    return {
      text: this.text,
    };
  }

  toString() {
    return this.user!.toString() + " comment";
  }
}

export enum DecisionState {
  awaiting = "awaiting",
  accepted = "accepted",
  rejected = "rejected",
}

export class Decision extends ResourceItem {
  subjectApp!: string;
  subjectResource!: string;

  @apiField()
  subjectType!: string;

  @dateApiField({ readOnly: true })
  resolvedAt!: Date | null;
  @dateApiField({ readOnly: true })
  createdAt!: Date | null;

  @apiField({ readOnly: true })
  createdBy!: ResourceItem | null;
  @apiField({ readOnly: true })
  resolvedBy!: ResourceItem | null;

  @apiField()
  title!: string;

  @apiField()
  description!: string;

  @apiField({ readOnly: true })
  str?: string;

  @itemArrayApiField(DecisionComment, { readOnly: true })
  comments?: DecisionComment[];
  @itemArrayApiField(DecisionVote, { readOnly: true })
  votes?: DecisionVote[];
  @itemApiField(DecisionVote, { readOnly: true })
  myVote?: DecisionVote;

  @apiField({ readOnly: true })
  state!: DecisionState;

  @apiField({ readOnly: true })
  startingCount?: number;

  @apiField({ readOnly: true })
  upvoteCount!: number;
  @apiField({ readOnly: true })
  downvoteCount!: number;

  public starting!: DecisionChange<any>;

  public revisionResource!: Resource<RevisionActions>;

  constructor(data?: any) {
    super(data);
    if (data) {
      [this.subjectApp, this.subjectResource] = data.subjectType.split(".");

      constructFromApiData(this, data);

      if (data.subjectPayload) {
        this.revisionResource = (apps as any)[this.subjectApp].revisionMetadata[
          this.subjectResource
        ].resource;

        const revisionConsturctor = (apps as any)[this.subjectApp]
          .revisionMetadata[this.subjectResource].constructor;

        this.starting = {
          new: new revisionConsturctor(data.subjectPayload.new),
          previous: data.subjectPayload.previous
            ? new revisionConsturctor(data.subjectPayload.previous)
            : null,
        };
      }
    }

    if (!this.myVote) this.myVote = new DecisionVote();
  }

  getPostData() {
    return getApiPostData(this);
  }
}

export interface DecisionChange<T> {
  new: T;
  previous: T;
}

const decisionApiUrl = "management/decision/";

export class SimpleDecision extends ResourceItem {
  subjectApp: string;
  subjectResource: string;
  state: DecisionState;
  title: string;
  description: string;

  upvoteCount: number;
  downvoteCount: number;

  constructor(data: any) {
    super(data);
    [this.subjectApp, this.subjectResource] = data.subjectType.split(".");
    this.state = data.state;
    this.title = data.title;
    this.description = data.description;
    this.upvoteCount = data.upvoteCount;
    this.downvoteCount = data.downvoteCount;
  }
}

export const decisionResource = (client: MegaraxClient) => ({
  ...createDeleteResource(decisionApiUrl)(client),
  ...createListResource(
    decisionApiUrl,
    (data) => new SimpleDecision(data),
  )(client),
  ...createDetailResource(
    decisionApiUrl,
    (data: any) => new Decision(data),
  )(client),
  async approveDecision(item: Decision) {
    const resp = await client.post(`${decisionApiUrl}${item.id}/approve/`, {});
    return new Decision(resp.data);
  },

  async rejectDecision(item: Decision): Promise<Decision> {
    const resp = await client.post(`${decisionApiUrl}${item.id}/reject/`, {});
    return new Decision(resp.data);
  },

  async setDecisionVote(decisionItem: Decision, status: boolean) {
    const newVote = new DecisionVote();
    newVote.id = decisionItem.myVote ? decisionItem.myVote.id : undefined;
    newVote.status = status;
    newVote.decisionId = decisionItem.id;

    const resp = await client.post(
      decisionApiUrl + `${decisionItem.id}/set_vote/`,
      newVote.getPostData(),
    );

    const receivedVote = new DecisionVote(resp.data);

    decisionItem.myVote = receivedVote;
    const myVoteIndex = decisionItem.votes!.findIndex(
      (o) => o.id == receivedVote.id,
    );

    // update user vote on the list of all votes
    if (myVoteIndex != -1) decisionItem.votes![myVoteIndex] = receivedVote;
    else decisionItem.votes!.push(receivedVote);

    return decisionItem;
  },

  async deleteDecisionVote(decisionItem: Decision) {
    if (!decisionItem.myVote) throw "No user vote";
    const voteId = decisionItem.myVote.id!;

    client.delete(decisionApiUrl + decisionItem.id + "/delete_vote/");

    decisionItem.myVote = new DecisionVote();

    // remove the vote from the list of all votes
    const myVoteIndex = decisionItem.votes!.findIndex((o) => o.id == voteId);

    decisionItem.votes!.splice(myVoteIndex, 1);

    return decisionItem;
  },

  async addDecisionComment(decisionItem: Decision, text: string) {
    const newComment = new DecisionComment();
    newComment.text = text;

    const response = await client.post(
      decisionApiUrl + `${decisionItem.id}/add_comment/`,
      newComment.getPostData(),
    );

    const receivedComment = new DecisionComment(response.data);

    // add comment to the decision object
    decisionItem.comments!.push(receivedComment);

    return decisionItem;
  },
});
